import React, { useEffect, useState, useMemo } from 'react';
import AdminEventForm from './AdminEventForm';
import { DAY_THEMES } from '../constants/cycleLegend';
import './Calendar.css';

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function daysInMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null); // null = closed, {} = create, {...} = edit
  const [deleteError, setDeleteError] = useState('');
  const [listFilter, setListFilter] = useState('upcoming'); // all | upcoming | past
  const [siteContent, setSiteContent] = useState({});
  const [editingContent, setEditingContent] = useState(null); // null | { key, title, items }

  // Config-driven so the same generic card renderer + edit modal handles
  // all five sections regardless of shape (single paragraph, labeled
  // address block, or a list of named contacts).
  const CONTENT_SECTIONS = [
    { key: 'calendar_location', title: 'Location & contact' },
    { key: 'calendar_about', title: 'About Word.Sound.Life.' },
    { key: 'calendar_volunteer', title: 'Volunteer with the media crew' },
    { key: 'calendar_urban_griots', title: 'Urban Griots', wide: true },
    { key: 'calendar_contacts', title: 'To register', wide: true },
  ];

  useEffect(() => {
    const keys = CONTENT_SECTIONS.map((s) => s.key).join(',');
    fetch(`/api/site-content?keys=${keys}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.success) setSiteContent(data.content || {});
      })
      .catch((err) => console.error('Error fetching site content:', err));
  }, []);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  }, []);
  const isAdmin = !!user?.admin;

  useEffect(() => {
    fetch('/api/calendar-events')
      .then((r) => r.json())
      .then((data) => {
        if (data.success) setEvents(data.events || []);
        else setError(data.message || 'Failed to load events');
      })
      .catch((err) => {
        console.error('Error fetching calendar events:', err);
        setError('Failed to load events');
      })
      .finally(() => setLoading(false));
  }, []);

  const grid = useMemo(() => {
    const first = startOfMonth(viewDate);
    const totalDays = daysInMonth(viewDate);
    const leadingBlanks = first.getDay();
    const cells = [];
    for (let i = 0; i < leadingBlanks; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) {
      cells.push(new Date(viewDate.getFullYear(), viewDate.getMonth(), d));
    }
    return cells;
  }, [viewDate]);

  function eventsForDate(date) {
    if (!date) return [];
    const dow = date.getDay();
    const recurring = events.filter((e) => e.recurrence === 'weekly' && e.day_of_week === dow);
    const oneOff = events.filter((e) => {
      if (e.recurrence !== 'none' || !e.starts_at) return false;
      const eventDate = new Date(e.starts_at);
      return (
        eventDate.getFullYear() === date.getFullYear() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getDate() === date.getDate()
      );
    });
    return [...recurring, ...oneOff];
  }

  function changeMonth(delta) {
    setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));
    setSelectedDay(null);
  }

  // One-off events split by whether their real date has passed.
  // Recurring weekly events don't have a single date to be "past" —
  // they're always shown in upcoming, with their day/time pattern
  // instead of a specific date.
  const { upcomingList, pastList } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const recurring = events.filter((e) => e.recurrence === 'weekly');
    const oneOff = events.filter((e) => e.recurrence === 'none' && e.starts_at);
    const upcomingOneOff = [];
    const pastOneOff = [];
    oneOff.forEach((e) => {
      const d = new Date(e.starts_at);
      (d >= today ? upcomingOneOff : pastOneOff).push(e);
    });
    upcomingOneOff.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
    pastOneOff.sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at));
    return {
      upcomingList: [...recurring, ...upcomingOneOff],
      pastList: pastOneOff,
    };
  }, [events]);

  function formatTime(startsAt) {
    if (!startsAt) return '';
    return new Date(startsAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  // DB rows are snake_case (starts_at, day_of_week, rsvp_enabled);
  // AdminEventForm expects camelCase (startsAt, dayOfWeek, rsvpEnabled).
  // startsAt also needs to be trimmed to the datetime-local input's
  // expected "YYYY-MM-DDTHH:mm" format, not a full ISO/timestamp string.
  function toFormShape(row) {
    if (!row) return null;
    return {
      id: row.id,
      title: row.title,
      description: row.description || '',
      location: row.location || '',
      capacity: row.capacity || '',
      rsvpEnabled: row.rsvp_enabled || false,
      recurrence: row.recurrence || 'none',
      dayOfWeek: row.day_of_week,
      startsAt: row.starts_at ? new Date(row.starts_at).toISOString().slice(0, 16) : '',
    };
  }

  function handleSaved(saved) {
    setEvents((prev) => {
      const exists = prev.some((e) => e.id === saved.id);
      return exists ? prev.map((e) => (e.id === saved.id ? saved : e)) : [saved, ...prev];
    });
    setEditingEvent(null);
  }

  async function handleDelete(event) {
    if (!window.confirm(`Delete "${event.title}"? This can't be undone.`)) return;
    setDeleteError('');
    try {
      const res = await fetch(`/api/admin/events/${event.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setEvents((prev) => prev.filter((e) => e.id !== event.id));
      } else {
        setDeleteError(data.message || 'Failed to delete event');
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      setDeleteError('Failed to delete event');
    }
  }

  async function handleSaveContent() {
    if (!editingContent) return;
    try {
      const res = await fetch(`/api/site-content/${editingContent.key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: editingContent.items }),
      });
      const data = await res.json();
      if (data.success) {
        setSiteContent((prev) => ({ ...prev, [data.key]: data.value }));
        setEditingContent(null);
      }
    } catch (err) {
      console.error('Error saving content:', err);
    }
  }

  function updateEditingItem(index, field, value) {
    setEditingContent((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  }

  function addEditingItem() {
    setEditingContent((prev) => ({ ...prev, items: [...prev.items, { label: '', text: '' }] }));
  }

  function removeEditingItem(index) {
    setEditingContent((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  }

  // Simple heuristic linkification for the read-only view — full URLs
  // and things that look like an email address become real links.
  function renderText(text) {
    if (!text) return null;
    if (text.startsWith('http')) {
      return <a href={text} target="_blank" rel="noopener noreferrer">{text}</a>;
    }
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text.trim())) {
      return <a href={`mailto:${text.trim()}`}>{text}</a>;
    }
    return text;
  }

  const monthLabel = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const today = new Date();
  const selectedEvents = selectedDay ? eventsForDate(selectedDay) : [];

  return (

<div className="cal-view">
      <div className="cal-header">
        <img className="tour-promo-image-placeholder" src="images/wsl-logo-trans.png" alt="Word. Sound. Life." />
        <h1>Community Calendar</h1>
        <div className="cal-nav">
          <button onClick={() => changeMonth(-1)} aria-label="Previous month">‹</button>
          <span className="cal-month-label">{monthLabel}</span>
          <button onClick={() => changeMonth(1)} aria-label="Next month">›</button>
        </div>
        <div className="cal-list-filters">
          {['all', 'upcoming', 'past'].map((key) => (
            <button
              key={key}
              className={'cal-list-filter-btn' + (listFilter === key ? ' active' : '')}
              onClick={() => setListFilter(key)}
            >
              {key.charAt(0).toUpperCase() + key.slice(1)}
            </button>
          ))}
        </div>
      </div>
      

          <div className="cal-list-section">
            {(listFilter === 'all' || listFilter === 'upcoming') && (
              <>
                <div className="cal-list-label">Upcoming</div>
                {upcomingList.length === 0 ? (
                  <p className="cal-list-empty">Nothing upcoming right now.</p>
                ) : (
                  <div className="cal-list">
                    {upcomingList.map((e) => (
                      <div className="cal-list-row" key={e.id}>
                        {e.imagepath && (
                          <img className="cal-list-row-thumb" src={e.imagepath} alt="" />
                        )}
                        <div className="cal-list-row-date">
                          {e.recurrence === 'weekly'
                            ? DAY_THEMES[e.day_of_week]?.label
                            : new Date(e.starts_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                        <div className="cal-list-row-body">
                          <div className="cal-list-row-title">{e.title}</div>
                          <div className="cal-list-row-meta">
                            {formatTime(e.starts_at)}
                            {e.location && ` · ${e.location}`}
                            {e.sponsor && ` · ${e.sponsor}`}
                            {e.recurrence === 'weekly' && ' · Weekly'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {(listFilter === 'all' || listFilter === 'past') && (
              <>
                <div className="cal-list-label">Past</div>
                {pastList.length === 0 ? (
                  <p className="cal-list-empty">No past events on record.</p>
                ) : (
                  <div className="cal-list">
                    {pastList.map((e) => (
                      <div className="cal-list-row cal-list-row--past" key={e.id}>
                        {e.imagepath && (
                          <img className="cal-list-row-thumb" src={e.imagepath} alt="" />
                        )}
                        <div className="cal-list-row-date">
                          {new Date(e.starts_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                        <div className="cal-list-row-body">
                          <div className="cal-list-row-title">{e.title}</div>
                          {e.location && <div className="cal-list-row-meta">{e.location}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
<br/><br/>
      {error && <div className="cal-error">{error}</div>}
      {loading ? (
        <div className="cal-loading"><div className="cal-spinner" /></div>
      ) : (
        <>
          <div className="cal-grid">
            {DAY_THEMES.map((d) => (
              <div className="cal-theme-header" key={d.label}>
                <div className="cal-theme-day">{d.label}</div>
                <div className="cal-theme-label">{d.theme}</div>
              </div>
            ))}

            {grid.map((date, i) => {
              if (!date) return <div className="cal-cell cal-cell--blank" key={`blank-${i}`} />;
              const dayEvents = eventsForDate(date);
              const isToday =
                date.getFullYear() === today.getFullYear() &&
                date.getMonth() === today.getMonth() &&
                date.getDate() === today.getDate();
              const isSelected = selectedDay && date.getTime() === selectedDay.getTime();
              return (
                <button
                  key={date.toISOString()}
                  className={
                    'cal-cell' +
                    (isToday ? ' cal-cell--today' : '') +
                    (isSelected ? ' cal-cell--selected' : '') +
                    (dayEvents.length ? ' cal-cell--has-events' : '')
                  }
                  onClick={() => setSelectedDay(date)}
                >
                  <span className="cal-cell-date">{date.getDate()}</span>
                  <div className="cal-cell-events">
                    {dayEvents.slice(0, 3).map((e) => (
                      <div className="cal-cell-event" key={e.id}>{e.title}</div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="cal-cell-more">+{dayEvents.length - 3} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {selectedDay && (
            <div className="cal-modal-overlay" onClick={() => setSelectedDay(null)}>
              <div className="cal-modal cal-day-modal" onClick={(e) => e.stopPropagation()}>
                <button className="cal-modal-close" onClick={() => setSelectedDay(null)}>✕</button>
                <div className="cal-day-detail-header">
                  <h2>
                    {selectedDay.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </h2>
                  {isAdmin && (
                    <button className="cal-add-btn" onClick={() => setEditingEvent({})}>+ Add event</button>
                  )}
                </div>

                {deleteError && <div className="cal-error">{deleteError}</div>}

                {selectedEvents.length === 0 ? (
                  <p className="cal-day-empty">Nothing scheduled.</p>
                ) : (
                  <div className="cal-day-list">
                    {selectedEvents.map((e) => (
                      <div className="cal-day-event" key={e.id}>
                        {e.imagepath && (
                          <img className="cal-day-event-flyer" src={e.imagepath} alt={e.title} />
                        )}
                        <div className="cal-day-event-time">{formatTime(e.starts_at)}</div>
                        <div className="cal-day-event-body">
                          <div className="cal-day-event-title">{e.title}</div>
                          {e.location && <div className="cal-day-event-location">{e.location}</div>}
                          {e.sponsor && <div className="cal-day-event-sponsor">Presented by {e.sponsor}</div>}
                          {e.description && <p className="cal-day-event-desc">{e.description}</p>}
                          {e.link && (
                            <a className="cal-day-event-link" href={e.link} target="_blank" rel="noopener noreferrer">
                              More info →
                            </a>
                          )}
                          {e.recurrence === 'weekly' && (
                            <span className="cal-day-event-recurring">Weekly</span>
                          )}
                        </div>
                        {isAdmin && (
                          <div className="cal-day-event-actions">
                            <button onClick={() => setEditingEvent(toFormShape(e))}>Edit</button>
                            <button onClick={() => handleDelete(e)}>Delete</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {editingEvent && (
            <div className="cal-modal-overlay" onClick={() => setEditingEvent(null)}>
              <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
                <button className="cal-modal-close" onClick={() => setEditingEvent(null)}>✕</button>
                <h3>{editingEvent.id ? 'Edit event' : 'Add event'}</h3>
                <AdminEventForm initialEvent={editingEvent} onSaved={handleSaved} />
              </div>
            </div>
          )}


          {/* <div className="cal-info-grid">
            {CONTENT_SECTIONS.map((section) => {
              const content = siteContent[section.key];
              const items = content?.items || [];
              return (
                <div className={'cal-info-card' + (section.wide ? ' cal-info-card--wide' : '')} key={section.key}>
                  <div className="cal-info-card-head">
                    <div className="cal-info-title">{section.title}</div>
                    {isAdmin && (
                      <button
                        className="cal-info-edit-btn"
                        onClick={() => setEditingContent({ key: section.key, title: section.title, items: items.length ? items : [{ label: '', text: '' }] })}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {items.map((item, i) => (
                    <p className={item.label ? 'cal-info-line' : 'cal-info-desc'} key={i}>
                      {item.label && <strong>{item.label}: </strong>}
                      {renderText(item.text)}
                    </p>
                  ))}
                </div>
              );
            })}
          </div> */}

          {editingContent && (
            <div className="cal-modal-overlay" onClick={() => setEditingContent(null)}>
              <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
                <button className="cal-modal-close" onClick={() => setEditingContent(null)}>✕</button>
                <h3>Edit: {editingContent.title}</h3>
                <div className="cal-content-editor">
                  {editingContent.items.map((item, i) => (
                    <div className="cal-content-editor-row" key={i}>
                      <input
                        type="text"
                        placeholder="Label (optional)"
                        value={item.label || ''}
                        onChange={(e) => updateEditingItem(i, 'label', e.target.value)}
                        className="cal-content-editor-label"
                      />
                      <textarea
                        placeholder="Text"
                        rows={2}
                        value={item.text || ''}
                        onChange={(e) => updateEditingItem(i, 'text', e.target.value)}
                        className="cal-content-editor-text"
                      />
                      <button className="cal-content-editor-remove" onClick={() => removeEditingItem(i)} aria-label="Remove">✕</button>
                    </div>
                  ))}
                  <button className="cal-content-editor-add" onClick={addEditingItem}>+ Add line</button>
                </div>
                <button className="mihu-btn-primary-lg" onClick={handleSaveContent}>Save</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
